import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
  Send, Loader2, Mic, MicOff,
  RefreshCw, Paperclip, ChevronDown,
  Brain, Sparkles, Wrench, Cpu, CheckCircle2, Search, Lightbulb, Layers, Zap,
} from "lucide-react";
import { useSpeechToText } from "@/hooks/useAudio";



/* ─── Interactive Thinking Indicator (Emergent-style) ─── */
const THINKING_STAGES = [
  { icon: Search, label: "Analyzing your request", color: "#014d7d" },
  { icon: Brain, label: "Thinking deeply", color: "#6941c6" },
  { icon: Layers, label: "Structuring content", color: "#178a55" },
  { icon: Sparkles, label: "Generating response", color: "#c8841a" },
  { icon: Wrench, label: "Tightening the screws", color: "#b84040" },
  { icon: Zap, label: "Finalizing", color: "#0e7490" },
];

const ThinkingIndicator = ({ agentActivities }) => {
  const [stageIdx, setStageIdx] = useState(0);
  const [dots, setDots] = useState("");

  useEffect(() => {
    const stageTimer = setInterval(() => {
      setStageIdx(prev => (prev + 1) % THINKING_STAGES.length);
    }, 2800);
    const dotTimer = setInterval(() => {
      setDots(prev => prev.length >= 3 ? "" : prev + ".");
    }, 400);
    return () => { clearInterval(stageTimer); clearInterval(dotTimer); };
  }, []);

  const stage = THINKING_STAGES[stageIdx];
  const StageIcon = stage.icon;
  const hasActivities = agentActivities?.length > 0;
  const latestActivity = hasActivities ? agentActivities[agentActivities.length - 1] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="flex justify-start mb-3"
      data-testid="thinking-indicator"
    >
      <div className="max-w-[90%]">
        <div className="bg-white border border-border/50 rounded-2xl rounded-bl-md shadow-sm overflow-hidden">
          {/* Main thinking stage */}
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="relative">
              <motion.div
                key={stageIdx}
                initial={{ scale: 0.5, opacity: 0, rotate: -90 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.5, opacity: 0, rotate: 90 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: `${stage.color}12` }}
              >
                <StageIcon className="w-4 h-4" style={{ color: stage.color }} />
              </motion.div>
              {/* Pulsing ring */}
              <motion.div
                className="absolute inset-0 rounded-lg"
                style={{ border: `2px solid ${stage.color}` }}
                animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <AnimatePresence mode="wait">
                <motion.p
                  key={stageIdx}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  className="text-sm font-medium"
                  style={{ color: stage.color }}
                >
                  {latestActivity?.action || stage.label}{dots}
                </motion.p>
              </AnimatePresence>
              {/* Progress bar */}
              <div className="mt-1.5 h-1 bg-gray-100 rounded-full overflow-hidden w-40">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: `linear-gradient(90deg, ${stage.color}, ${THINKING_STAGES[(stageIdx + 1) % THINKING_STAGES.length].color})` }}
                  animate={{ width: ["0%", "100%"] }}
                  transition={{ duration: 2.8, ease: "easeInOut" }}
                  key={stageIdx}
                />
              </div>
            </div>
          </div>

          {/* Agent activities log */}
          {hasActivities && agentActivities.length > 1 && (
            <div className="px-4 pb-3 pt-1 border-t border-border/30">
              <div className="space-y-1">
                {agentActivities.slice(-3)?.map((a, i) => (
                  <motion.div
                    key={`${a.agent}-${i}`}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-2 text-[11px]"
                  >
                    {a.status === "completed" ? (
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                    ) : a.status === "in_progress" ? (
                      <Loader2 className="w-3 h-3 text-[#014d7d] animate-spin shrink-0" />
                    ) : (
                      <Cpu className="w-3 h-3 text-foreground/50 shrink-0" />
                    )}
                    <span className="font-medium text-foreground/50">{a.agent}</span>
                    <span className="text-foreground/50 truncate">{a.action}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const MessageBubble = ({ msg, onRegenerate, isLast }) => {
  const isUser = msg.role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}
      data-testid={`message-${msg.id}`}
    >
      <div className={`max-w-[90%] ${isUser ? "order-2" : ""}`}>
        <div className={`px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-[#014d7d] text-white rounded-2xl rounded-br-md"
            : "bg-white border border-border/40 text-foreground/80 rounded-2xl rounded-bl-md shadow-sm"
        }`}>
          {isUser ? (
            <p>{msg.content}</p>
          ) : (
            <div className="prose-compact">
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                  strong: ({ children }) => <strong className={`font-semibold ${isUser ? "text-white" : "text-foreground/90"}`}>{children}</strong>,
                  ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  code: ({ children }) => <code className="px-1 py-0.5 bg-foreground/5 rounded text-xs font-mono">{children}</code>,
                  h1: ({ children }) => <h3 className="font-semibold text-foreground/90 mt-2 mb-1">{children}</h3>,
                  h2: ({ children }) => <h3 className="font-semibold text-foreground/90 mt-2 mb-1">{children}</h3>,
                  h3: ({ children }) => <h4 className="font-medium text-foreground/85 mt-1.5 mb-0.5">{children}</h4>,
                }}
              >
                {msg.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
        {msg.follow_up_question && (
          <div className="mt-2 px-4 py-2.5 bg-amber-50/60 border border-amber-200/50 rounded-xl text-xs text-foreground/65 italic">
            {msg.follow_up_question}
          </div>
        )}
        {!isUser && isLast && (
          <div className="flex items-center gap-1 mt-1.5 ml-1">
            <button
              onClick={() => onRegenerate(msg.id)}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] text-foreground/40 hover:text-foreground/60 hover:bg-foreground/[0.03]"
              data-testid={`regenerate-${msg.id}`}
            >
              <RefreshCw className="w-2.5 h-2.5" /> Regenerate
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const ConversationPanel = ({ messages, onSend, onRegenerate, isSending, streamingText, sessionGoal, projectId, agentActivities }) => {
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const { isListening, startListening, stopListening } = useSpeechToText();

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening((text) => {
        setInput(text);
      });
    }
  }, [isListening, startListening, stopListening]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamingText, isSending]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      setShowScrollBtn(!isNearBottom);
    }
  }, []);

  const scrollToBottom = () => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!input.trim() || isSending) return;
    onSend(input.trim());
    setInput("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const lastAssistantMsg = [...messages].reverse().find((m) => m.role === "assistant");

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="conversation-panel">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-1 scroll-smooth min-h-0" onScroll={handleScroll} data-testid="messages-scroll-area">
        {messages.length === 0 && !streamingText && !isSending && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
            <div className="w-10 h-10 rounded-xl bg-[#014d7d]/10 flex items-center justify-center mb-3">
              <Send className="w-4 h-4 text-[#014d7d]/50" />
            </div>
            <p className="text-xs text-foreground/50 max-w-[200px]">
              {sessionGoal ? `Working on: "${sessionGoal}"` : "Start a conversation to create learning content."}
            </p>
          </div>
        )}

        {messages?.map((msg) => (
          <MessageBubble
            key={msg.id} msg={msg} onRegenerate={onRegenerate}
            isLast={!isSending && msg.role === "assistant" && msg.id === lastAssistantMsg?.id}
          />
        ))}

        {streamingText && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start mb-3">
            <div className="max-w-[90%] px-4 py-3 bg-white border border-border/40 rounded-2xl rounded-bl-md shadow-sm text-sm text-foreground/80">
              <ReactMarkdown>{streamingText}</ReactMarkdown>
            </div>
          </motion.div>
        )}

        <AnimatePresence>
          {isSending && !streamingText && <ThinkingIndicator agentActivities={agentActivities} />}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showScrollBtn && (
          <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            onClick={scrollToBottom}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-white border border-border/60 shadow-md flex items-center justify-center z-20 hover:bg-gray-50"
            data-testid="scroll-to-bottom">
            <ChevronDown className="w-3.5 h-3.5 text-foreground/50" />
          </motion.button>
        )}
      </AnimatePresence>

      <div className="px-3 pb-3 pt-2 bg-slate-50/80 border-t border-border/40 shrink-0" data-testid="chat-input-area">
        <form onSubmit={handleSubmit} className="relative">
          <div className="relative bg-white rounded-xl border border-border/60 focus-within:border-[#014d7d]/30 focus-within:ring-2 focus-within:ring-[#014d7d]/10 shadow-sm">
            <textarea
              ref={textareaRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Type your message..." disabled={isSending}
              className="w-full resize-none outline-none text-sm text-foreground placeholder:text-foreground/50 px-4 pt-3 pb-10 bg-transparent rounded-xl leading-relaxed"
              rows={2} style={{ minHeight: "80px", maxHeight: "200px" }}
              onInput={(e) => { e.target.style.height = "80px"; e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px"; }}
              data-testid="chat-input"
            />
            <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button type="button" className="w-7 h-7 rounded-lg text-foreground/50 hover:text-foreground/60 hover:bg-foreground/5 flex items-center justify-center" data-testid="attach-btn" title="Attach file">
                  <Paperclip className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={toggleListening}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center ${isListening ? "bg-red-50 text-red-500" : "text-foreground/50 hover:text-foreground/60 hover:bg-foreground/5"}`}
                  data-testid="mic-btn" title={isListening ? "Stop recording" : "Voice input"}>
                  {isListening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                </button>
              </div>
              <button type="submit" disabled={!input.trim() || isSending}
                className="w-7 h-7 rounded-lg bg-[#014d7d] text-white flex items-center justify-center disabled:opacity-20 hover:bg-[#013d66] active:scale-95"
                data-testid="send-btn">
                {isSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
